import { useMemo } from 'react';
import type { CodeSearchGroup, CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useVaccinationSearch, type VaccineValueSet } from '@/shared/services/use-vaccination-search';
import { useLabSearch } from '@/encounter/services/use-lab-search';
import { useRadiologySearch } from '@/encounter/services/use-radiology-search';
import { useMedicationValueSetSearch } from '@/encounter/services/use-medication-valueset-search';
import { useProcedureSearch } from '@/encounter/services/use-procedure-search';
import { useArticleSearch } from '@/encounter/services/use-article-search';
import type { MedicationOption } from '@/encounter/lib/to-medication-result';
import type { EducationArticle } from '@/encounter/lib/to-education-article';

/**
 * The order set builder's one search, "Search labs, imaging, medications, procedures, vaccines,
 * education" (PROD-2714, EditModal board).
 *
 * The SAME sources as the A/P rail's GLOBAL section (`useApCategorySearch`), one hook per type, all
 * queried with the one query: labs `/Master/LabOrderCodesList`, imaging the master radiology list,
 * medications the GLOBAL medication value-set, procedures master CPT, vaccines the DDC value-set,
 * education `GetArticles`. Each result keeps the RAW row the A/P hands its order form, so picking
 * one opens that form exactly as a rail pick does.
 *
 * Medications (PROD-2739): the global catalogue `GET /api/Master/SearchValueSets2?SearchFor=
 * Medications`, NOT the provider's `GetMedication` favourites, so a set can hold a medication that
 * is not yet anyone's favourite. It is Flutter's global medication search, org-scoped on the server
 * through the OrganizationId header (Vidal, DOH or DDC, from the org's MedicationSystem). The hook
 * already maps each row onto the `MedicationOption` the dose form takes: code, name, the row's OWN
 * system (never a hard-coded one), route[] and unitOfMeasurement. It needs 2 characters.
 */
export type OrderSearchPick =
  | { type: 'lab'; item: CodeSearchResult }
  | { type: 'imaging'; item: CodeSearchResult }
  | { type: 'procedure'; item: CodeSearchResult }
  | { type: 'medication'; item: MedicationOption }
  | { type: 'vaccine'; item: VaccineValueSet }
  | { type: 'education'; item: EducationArticle };

/** Debounce shared by every type (the A/P rail debounces education by 600ms, the dx search by 250). */
const DEBOUNCE_MS = 300;

/** The medication value-set search's own minimum (`use-medication-valueset-search.ts`). Below it the
 *  hook is disabled but keeps its previous rows as placeholder data, so the builder drops them. */
const MEDICATION_MIN_QUERY = 2;

/** The group headers, in the placeholder's order. */
const GROUP_LABEL: Record<OrderSearchPick['type'], string> = {
  lab: 'Labs',
  imaging: 'Imaging',
  medication: 'Medications',
  procedure: 'Procedures',
  vaccine: 'Vaccines',
  education: 'Education',
};

const TAG: Record<OrderSearchPick['type'], string> = {
  lab: 'LAB',
  imaging: 'IMG',
  medication: 'MED',
  procedure: 'CPT',
  vaccine: 'VAX',
  education: 'EDU',
};

/** A code as the builder compares it across sources: trimmed, case-insensitive. */
function normalizeCode(code: string | undefined): string {
  return (code ?? '').trim().toLowerCase();
}

/**
 * PROD-2829: a lab catalogued by its CPT code (e.g. 80069 Renal function panel) comes back from BOTH
 * the lab list and master CPT. One result set lists it once, under Labs, so picking it opens the lab
 * form. Procedure rows whose code is also a lab row's code are dropped; procedure-only codes (4079F)
 * stay. Codes match trimmed and case-insensitively; a procedure row with no code is kept.
 */
export function withoutLabDuplicates(
  labs: readonly CodeSearchResult[],
  procedures: readonly CodeSearchResult[],
): CodeSearchResult[] {
  const labCodes = new Set(labs.map((r) => normalizeCode(r.code)).filter((c) => c.length > 0));
  if (labCodes.size === 0) return [...procedures];
  return procedures.filter((r) => {
    const code = normalizeCode(r.code);
    return code.length === 0 || !labCodes.has(code);
  });
}

export interface OrderSetOrderSearch {
  groups: CodeSearchGroup[];
  loading: boolean;
  /** The pick behind a result the field handed back (its id is `<type>:<row id>`). */
  resolve: (result: CodeSearchResult) => OrderSearchPick | null;
}

export function useOrderSetOrderSearch(query: string, enabled = true): OrderSetOrderSearch {
  const debounced = useDebounce(query, DEBOUNCE_MS);
  const q = enabled ? debounced.trim() : '';

  const lab = useLabSearch(undefined, q);
  const imaging = useRadiologySearch(undefined, q);
  const medication = useMedicationValueSetSearch(q);
  const procedure = useProcedureSearch(undefined, 'cpt', q);
  const vaccine = useVaccinationSearch(q, q.length > 0);
  const education = useArticleSearch(q, q.length > 0);

  return useMemo(() => {
    const picks = new Map<string, OrderSearchPick>();
    const rows: Record<OrderSearchPick['type'], CodeSearchResult[]> = {
      lab: [], imaging: [], medication: [], procedure: [], vaccine: [], education: [],
    };
    const add = (pick: OrderSearchPick, rowId: string, display: string, code?: string, system?: string) => {
      const id = `${pick.type}:${rowId}`;
      if (!display || picks.has(id)) return;
      picks.set(id, pick);
      rows[pick.type].push({
        id,
        display,
        ...(code ? { code } : {}),
        ...(system ? { system } : {}),
        badge: TAG[pick.type],
      });
    };

    if (q) {
      for (const r of lab.data ?? []) add({ type: 'lab', item: r }, r.id, r.display, r.code, r.system);
      for (const r of imaging.data ?? []) add({ type: 'imaging', item: r }, r.id, r.display, r.code, r.system);
      if (q.length >= MEDICATION_MIN_QUERY) {
        for (const m of medication.data ?? []) add({ type: 'medication', item: m }, m.id, m.name, m.code, m.system);
      }
      for (const r of withoutLabDuplicates(lab.data ?? [], procedure.data ?? [])) {
        add({ type: 'procedure', item: r }, r.id, r.display, r.code, r.system);
      }
      (vaccine.data ?? []).forEach((v, i) => {
        const code = (v.valueCoding?.code ?? '').trim();
        const display = (v.valueCoding?.display ?? '').trim();
        add({ type: 'vaccine', item: v }, code || `${display}-${i}`, display || code, code, v.valueCoding?.system);
      });
      for (const g of education.data ?? []) {
        for (const a of g.articles ?? []) {
          if (!a.display) continue;
          add(
            { type: 'education', item: { display: a.display, articleUrl: a.articleUrl, summary: a.summary } },
            a.id ?? a.display,
            a.display,
          );
        }
      }
    }

    const groups: CodeSearchGroup[] = (Object.keys(GROUP_LABEL) as OrderSearchPick['type'][])
      .filter((t) => rows[t].length > 0)
      .map((t) => ({ label: GROUP_LABEL[t], results: rows[t] }));

    return {
      groups,
      loading:
        q.length > 0 &&
        (lab.isFetching || imaging.isFetching || medication.isFetching || procedure.isFetching ||
          vaccine.isFetching || education.isFetching),
      resolve: (result: CodeSearchResult) => picks.get(result.id) ?? null,
    };
  }, [q, lab.data, imaging.data, medication.data, procedure.data, vaccine.data, education.data,
    lab.isFetching, imaging.isFetching, medication.isFetching, procedure.isFetching, vaccine.isFetching,
    education.isFetching]);
}
